import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useClient } from '@/contexts/ClientContext';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type {
  DueToDecomposition,
  CannibalizationResult,
  EnhancedTURFResult,
  SensitivityMarketOverview,
} from '@/api/client';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { TabGroup, DonutChart, HorizontalBarChart, SimpleLineChart, CollapsibleSection, AccessibleDonut, AccessibleBar } from '@/components/ui';

// Shared layout wrapper for detail pages
function DetailPageLayout({
  title,
  subtitle,
  backLink,
  backLabel,
  children,
}: {
  title: string;
  subtitle?: string;
  backLink: string;
  backLabel: string;
  children: React.ReactNode;
}) {
  return (
    <div className="max-w-6xl mx-auto py-6 px-4">
      {/* Header */}
      <div className="mb-6">
        <Link
          to={backLink}
          className="inline-flex items-center gap-2 text-sm text-primary-400 hover:text-primary-300 mb-4"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {backLabel}
        </Link>
        <h1 className="text-2xl font-bold text-white">{title}</h1>
        {subtitle && <p className="text-text-secondary mt-1">{subtitle}</p>}
      </div>
      {/* Content */}
      {children}
    </div>
  );
}

// Loading content helper
function LoadingContent({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 gap-3">
      <LoadingSpinner size="md" />
      <p className="text-text-secondary text-sm">{message}</p>
    </div>
  );
}

// ============ UNDERSTAND DETAIL PAGES ============

// Market Landscape Detail with Size, Structure, Trends tabs
export function MarketLandscapeDetail() {
  const { language } = useLanguage();
  const { selectedClient } = useClient();
  const clientId = selectedClient?.client_id;
  const t = language === 'es';

  const { data: marketData, isLoading, error } = useQuery({
    queryKey: ['client', clientId, 'market', 'overview'],
    queryFn: () => apiClient.market.getOverview(clientId!),
    enabled: !!clientId,
  });

  if (!clientId) {
    return (
      <DetailPageLayout
        title={t ? 'Panorama del Mercado' : 'Market Landscape'}
        backLink="/understand"
        backLabel={t ? 'Volver a Entender' : 'Back to Understand'}
      >
        <div className="card p-8 text-center">
          <p className="text-text-secondary">{t ? 'Seleccione un cliente' : 'Select a client'}</p>
        </div>
      </DetailPageLayout>
    );
  }

  const tabs = [
    { id: 'size', label: t ? 'Tamaño' : 'Size' },
    { id: 'structure', label: t ? 'Estructura' : 'Structure' },
    { id: 'trends', label: t ? 'Tendencias' : 'Trends' },
  ];

  return (
    <DetailPageLayout
      title={t ? 'Panorama del Mercado' : 'Market Landscape'}
      subtitle={t ? 'Análisis detallado del tamaño y estructura del mercado' : 'Detailed analysis of market size and structure'}
      backLink="/understand"
      backLabel={t ? 'Volver a Entender' : 'Back to Understand'}
    >
      {isLoading && <LoadingContent message="Loading market data..." />}
      {error && <ErrorMessage error={error} />}
      {marketData && (
        <TabGroup tabs={tabs} defaultTab="size" variant="underline" size="md">
          {(activeTab) => (
            <div className="mt-6">
              {activeTab === 'size' && (
                <MarketSizeTab marketData={marketData} language={language} />
              )}
              {activeTab === 'structure' && (
                <MarketStructureTab marketData={marketData} language={language} />
              )}
              {activeTab === 'trends' && (
                <MarketTrendsTab marketData={marketData} language={language} />
              )}
            </div>
          )}
        </TabGroup>
      )}
    </DetailPageLayout>
  );
}

// Size Tab - Market size metrics and brand rankings
function MarketSizeTab({ marketData, language }: { marketData: any; language: string }) {
  const t = language === 'es';

  // Prepare donut chart data for brand share
  const donutData = marketData.top_brands?.slice(0, 5).map((brand: any) => ({
    name: brand.name,
    value: (brand.share || 0) * 100,
  })) || [];

  // Prepare horizontal bar data
  const barData = marketData.top_brands?.slice(0, 8).map((brand: any) => ({
    name: brand.name,
    value: (brand.share || 0) * 100,
    secondaryValue: brand.growth !== undefined ? (brand.growth * 100) : undefined,
  })) || [];

  return (
    <div className="space-y-6">
      {/* KPI Row */}
      <div className="grid grid-cols-4 gap-4">
        <div className="card p-4 text-center">
          <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">
            {t ? 'Valor Total' : 'Total Value'}
          </div>
          <div className="text-2xl font-bold text-white">
            {formatCurrency(marketData.market?.total_value || 0, marketData.market?.currency || 'USD')}
          </div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">
            {t ? 'Volumen Total' : 'Total Volume'}
          </div>
          <div className="text-2xl font-bold text-white">
            {formatNumber(marketData.market?.total_volume || 0)}
          </div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">
            {t ? 'Crecimiento' : 'Growth Rate'}
          </div>
          <div className={`text-2xl font-bold ${(marketData.market?.growth_rate || 0) >= 0 ? 'text-success-400' : 'text-danger-400'}`}>
            {((marketData.market?.growth_rate || 0) * 100).toFixed(1)}%
          </div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">
            {t ? 'Marcas Activas' : 'Active Brands'}
          </div>
          <div className="text-2xl font-bold text-white">
            {marketData.total_brands || 0}
          </div>
        </div>
      </div>

      {/* Donut + Bar Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Donut Chart - Wrapped for accessibility (WCAG 1.1.1) */}
        <div className="card p-6">
          <AccessibleDonut
            title={t ? 'Participación de Marca' : 'Brand Share'}
            data={donutData}
          >
            <DonutChart
              data={donutData}
              title={t ? 'Participación de Marca' : 'Brand Share'}
              centerValue={`${donutData.length}`}
              centerLabel={t ? 'Marcas' : 'Brands'}
              height={220}
            />
          </AccessibleDonut>
        </div>

        {/* Horizontal Bar Rankings - Wrapped for accessibility (WCAG 1.1.1) */}
        <div className="card p-6">
          <AccessibleBar
            title={t ? 'Ranking de Marcas' : 'Brand Rankings'}
            data={barData}
            valueLabel="Share %"
          >
            <HorizontalBarChart
              data={barData}
              title={t ? 'Ranking de Marcas' : 'Brand Rankings'}
              valueLabel="Share"
              secondaryLabel={t ? 'Crecimiento' : 'Growth'}
              showRank={true}
            />
          </AccessibleBar>
        </div>
      </div>

      {/* Growth Outlook */}
      {marketData.growth_outlook && (
        <div className="card p-6 bg-primary-500/5 border-primary-500/20">
          <div className="flex items-start gap-3">
            <span className="text-xl">💡</span>
            <div>
              <h3 className="text-sm font-medium text-primary-400 mb-1">
                {t ? 'Perspectiva de Crecimiento' : 'Growth Outlook'}
              </h3>
              <p className="text-text-secondary text-sm">{marketData.growth_outlook}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Structure Tab - Market breakdown by tier, format, occasion
function MarketStructureTab({ marketData, language }: { marketData: any; language: string }) {
  const t = language === 'es';

  // Mock structure data (in real app, this would come from API)
  const priceTierData = [
    { name: t ? 'Premium' : 'Premium', value: 35, color: '#38bdf8' },
    { name: t ? 'Medio' : 'Mid-Tier', value: 45, color: '#fbbf24' },
    { name: t ? 'Económico' : 'Economy', value: 20, color: '#6ee7b7' },
  ];

  const formatData = [
    { name: t ? 'Individual' : 'Single-Serve', value: 40, color: '#38bdf8' },
    { name: t ? 'Familiar' : 'Family Pack', value: 35, color: '#fbbf24' },
    { name: t ? 'Multipack' : 'Multipack', value: 15, color: '#fb7185' },
    { name: t ? 'A Granel' : 'Bulk', value: 10, color: '#a5b4fc' },
  ];

  const occasionData = [
    { name: t ? 'Desayuno' : 'Breakfast', value: 30, color: '#38bdf8' },
    { name: t ? 'Snack' : 'Snacking', value: 25, color: '#fbbf24' },
    { name: t ? 'Comida' : 'Meal', value: 20, color: '#6ee7b7' },
    { name: t ? 'Indulgencia' : 'Indulgence', value: 15, color: '#fb7185' },
    { name: t ? 'Otro' : 'Other', value: 10, color: '#a5b4fc' },
  ];

  // Reusable breakdown component with donut + legend
  const BreakdownSection = ({
    title,
    data,
    defaultOpen = true
  }: {
    title: string;
    data: { name: string; value: number; color: string }[];
    defaultOpen?: boolean;
  }) => (
    <CollapsibleSection title={title} defaultOpen={defaultOpen}>
      <div className="pt-3 grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
        {/* Donut Chart */}
        <div className="flex justify-center">
          <DonutChart
            data={data}
            height={160}
            showLegend={false}
            centerValue={`${data.length}`}
            centerLabel={t ? 'segmentos' : 'segments'}
          />
        </div>

        {/* Legend with bars */}
        <div className="space-y-2">
          {data.map((item, idx) => (
            <div key={item.name} className="group">
              <div className="flex items-center gap-2 mb-1">
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-sm text-white flex-1">{item.name}</span>
                <span className="text-sm font-semibold text-white">{item.value}%</span>
              </div>
              <div className="h-1.5 bg-surface-600 rounded-full overflow-hidden ml-5">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${item.value}%`, backgroundColor: item.color }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </CollapsibleSection>
  );

  return (
    <div className="space-y-4">
      {/* Price Tier Breakdown */}
      <BreakdownSection
        title={t ? 'Por Nivel de Precio' : 'By Price Tier'}
        data={priceTierData}
        defaultOpen={true}
      />

      {/* Format Breakdown */}
      <BreakdownSection
        title={t ? 'Por Formato' : 'By Format'}
        data={formatData}
        defaultOpen={true}
      />

      {/* Occasion Breakdown */}
      <BreakdownSection
        title={t ? 'Por Ocasión' : 'By Occasion'}
        data={occasionData}
        defaultOpen={false}
      />

      {/* Structure Insight */}
      <div className="card p-4 bg-primary-500/5 border-primary-500/20">
        <div className="flex items-start gap-3">
          <span className="text-lg">💡</span>
          <div>
            <p className="text-sm font-medium text-white mb-1">{t ? 'Insight Clave' : 'Key Insight'}</p>
            <p className="text-sm text-text-secondary">
              {t
                ? 'El segmento Mid-Tier domina con 45% del mercado. Oportunidad de crecimiento en Premium (+2.3pp YoY) impulsado por tendencias de premiumización.'
                : 'Mid-Tier segment dominates at 45% of market. Growth opportunity in Premium (+2.3pp YoY) driven by premiumization trends.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Trends Tab - Historical and projected trends
function MarketTrendsTab({ marketData, language }: { marketData: any; language: string }) {
  const t = language === 'es';

  // Mock historical data (in real app, this would come from API)
  const trendData = [
    { name: '2023 Q1', value: 480, volume: 115 },
    { name: '2023 Q2', value: 485, volume: 118 },
    { name: '2023 Q3', value: 492, volume: 120 },
    { name: '2023 Q4', value: 498, volume: 122 },
    { name: '2024 Q1', value: 505, volume: 123 },
    { name: '2024 Q2', value: 512, volume: 125 },
    { name: '2024 Q3', value: 518, volume: 126 },
    { name: '2024 Q4', value: 525, volume: 128 },
    { name: '2025 Q1', value: 532, volume: 129 },
    { name: '2025 Q2', value: 540, volume: 130 },
  ];

  const growthDrivers = [
    { label: t ? 'Premiumización' : 'Premiumization', change: '+2.3pp', positive: true },
    { label: t ? 'Consumidores Health-Conscious' : 'Health-Conscious Consumers', change: '+1.8pp', positive: true },
    { label: t ? 'Expansión E-commerce' : 'E-commerce Expansion', change: '+1.5pp', positive: true },
    { label: t ? 'Canal Tradicional' : 'Traditional Channel', change: '-0.8pp', positive: false },
  ];

  return (
    <div className="space-y-6">
      {/* Trend Line Chart */}
      <div className="card p-6">
        <SimpleLineChart
          data={trendData}
          lines={[
            { dataKey: 'value', name: t ? 'Valor ($M)' : 'Value ($M)', color: '#38bdf8' },
            { dataKey: 'volume', name: t ? 'Volumen (K)' : 'Volume (K)', color: '#fbbf24', dashed: false },
          ]}
          title={t ? 'Tendencia del Mercado (Últimos 3 Años)' : 'Market Trend (Last 3 Years)'}
          height={280}
          showGrid={true}
          showLegend={true}
        />
      </div>

      {/* Growth Drivers */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-white mb-4">
          {t ? 'Drivers de Crecimiento' : 'Growth Drivers'}
        </h3>
        <div className="space-y-3">
          {growthDrivers.map((driver, idx) => (
            <div key={idx} className="flex items-center justify-between p-3 bg-surface-700/50 rounded-lg">
              <div className="flex items-center gap-3">
                <span className={`text-lg ${driver.positive ? 'text-success-400' : 'text-danger-400'}`}>
                  {driver.positive ? '↑' : '↓'}
                </span>
                <span className="text-sm text-white">{driver.label}</span>
              </div>
              <span className={`text-sm font-medium ${driver.positive ? 'text-success-400' : 'text-danger-400'}`}>
                {driver.change}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Projection Card */}
      <div className="card p-6 bg-primary-500/5 border-primary-500/20">
        <div className="flex items-start gap-3">
          <span className="text-xl">📈</span>
          <div>
            <h3 className="text-sm font-medium text-primary-400 mb-1">
              {t ? 'Proyección 2026' : '2026 Projection'}
            </h3>
            <p className="text-text-secondary text-sm">
              {t
                ? 'El mercado proyecta crecer 4.2% en valor y 2.8% en volumen. La premiumización seguirá siendo el principal driver.'
                : 'Market projected to grow 4.2% in value and 2.8% in volume. Premiumization remains the main driver.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Consumer Intelligence Detail
export function ConsumerIntelligenceDetail() {
  const { language } = useLanguage();
  const { selectedClient } = useClient();
  const clientId = selectedClient?.client_id;

  const { data: segmentData, isLoading, error } = useQuery({
    queryKey: ['client', clientId, 'market', 'segments'],
    queryFn: () => apiClient.market.getSegments(clientId!),
    enabled: !!clientId,
  });

  if (!clientId) {
    return (
      <DetailPageLayout
        title={language === 'es' ? 'Inteligencia del Consumidor' : 'Consumer Intelligence'}
        backLink="/understand"
        backLabel={language === 'es' ? 'Volver a Entender' : 'Back to Understand'}
      >
        <div className="card p-8 text-center">
          <p className="text-text-secondary">{language === 'es' ? 'Seleccione un cliente' : 'Select a client'}</p>
        </div>
      </DetailPageLayout>
    );
  }

  const segmentColors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

  return (
    <DetailPageLayout
      title={language === 'es' ? 'Inteligencia del Consumidor' : 'Consumer Intelligence'}
      subtitle={language === 'es' ? 'Perfiles detallados de segmentos de consumidores' : 'Detailed consumer segment profiles'}
      backLink="/understand"
      backLabel={language === 'es' ? 'Volver a Entender' : 'Back to Understand'}
    >
      {isLoading && <LoadingContent message="Loading segment data..." />}
      {error && <ErrorMessage error={error} />}
      {segmentData && (
        <div className="space-y-6">
          {/* Market Summary */}
          {segmentData.market_summary && (
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-white mb-4">
                {language === 'es' ? 'Resumen del Mercado' : 'Market Summary'}
              </h2>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-surface-700/50 rounded-lg p-4 text-center">
                  <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">
                    {language === 'es' ? 'Total Consumidores' : 'Total Consumers'}
                  </div>
                  <div className="text-2xl font-bold text-white">
                    {formatNumber(segmentData.market_summary.total_consumers)}
                  </div>
                </div>
                <div className="bg-surface-700/50 rounded-lg p-4 text-center">
                  <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">
                    {language === 'es' ? 'Penetración' : 'Penetration'}
                  </div>
                  <div className="text-2xl font-bold text-primary-400">
                    {(segmentData.market_summary.market_penetration * 100).toFixed(1)}%
                  </div>
                </div>
                <div className="bg-surface-700/50 rounded-lg p-4 text-center">
                  <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">
                    {language === 'es' ? 'Crecimiento Categoría' : 'Category Growth'}
                  </div>
                  <div className={`text-2xl font-bold ${segmentData.market_summary.category_growth >= 0 ? 'text-success-400' : 'text-danger-400'}`}>
                    {(segmentData.market_summary.category_growth * 100).toFixed(1)}%
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Compare Consumers Link */}
          <div className="flex justify-end">
            <Link
              to="/understand/compare-consumers"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500/10 hover:bg-primary-500/20 border border-primary-500/30 text-primary-400 text-sm font-medium rounded-lg transition-colors"
            >
              {language === 'es' ? 'Comparar Consumidores' : 'Compare Consumers'} →
            </Link>
          </div>

          {/* Segment Cards */}
          <div className="grid grid-cols-2 gap-6">
            {segmentData.segments?.map((segment: any, idx: number) => (
              <div
                key={segment.id}
                className="card p-6"
                style={{ borderLeft: `4px solid ${segmentColors[idx % segmentColors.length]}` }}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">{segment.name}</h3>
                  <span
                    className="text-xs px-2 py-1 rounded"
                    style={{ backgroundColor: `${segmentColors[idx % segmentColors.length]}20`, color: segmentColors[idx % segmentColors.length] }}
                  >
                    #{idx + 1}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <div className="text-xs text-text-tertiary">{language === 'es' ? 'Participación' : 'Share'}</div>
                    <div className="text-xl font-bold text-white">{((segment.share || 0) * 100).toFixed(1)}%</div>
                  </div>
                  <div>
                    <div className="text-xs text-text-tertiary">{language === 'es' ? 'Crecimiento' : 'Growth'}</div>
                    <div className={`text-xl font-bold ${(segment.growth || 0) >= 0 ? 'text-success-400' : 'text-danger-400'}`}>
                      {((segment.growth || 0) * 100).toFixed(1)}%
                    </div>
                  </div>
                </div>

                {segment.characteristics && segment.characteristics.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {segment.characteristics.slice(0, 4).map((char: string, charIdx: number) => (
                      <span key={charIdx} className="text-xs px-2 py-1 bg-surface-600 text-text-secondary rounded">
                        {char}
                      </span>
                    ))}
                  </div>
                )}

                {(segment.price_sensitivity !== undefined || segment.brand_loyalty !== undefined) && (
                  <div className="space-y-2">
                    {segment.price_sensitivity !== undefined && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-text-tertiary w-28">{language === 'es' ? 'Sens. Precio' : 'Price Sensitivity'}</span>
                        <div className="flex-1 h-2 bg-surface-600 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-warning-500 rounded-full"
                            style={{ width: `${(segment.price_sensitivity || 0) * 100}%` }}
                          />
                        </div>
                        <span className="text-xs text-text-secondary">{((segment.price_sensitivity || 0) * 100).toFixed(0)}%</span>
                      </div>
                    )}
                    {segment.brand_loyalty !== undefined && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-text-tertiary w-28">{language === 'es' ? 'Lealtad Marca' : 'Brand Loyalty'}</span>
                        <div className="flex-1 h-2 bg-surface-600 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary-500 rounded-full"
                            style={{ width: `${(segment.brand_loyalty || 0) * 100}%` }}
                          />
                        </div>
                        <span className="text-xs text-text-secondary">{((segment.brand_loyalty || 0) * 100).toFixed(0)}%</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </DetailPageLayout>
  );
}

// Competitive Position Detail
export function CompetitivePositionDetail() {
  const { language } = useLanguage();
  const { selectedClient } = useClient();
  const clientId = selectedClient?.client_id;

  const { data: position, isLoading, error } = useQuery({
    queryKey: ['client', clientId, 'market', 'position'],
    queryFn: () => apiClient.market.getPosition(clientId!),
    enabled: !!clientId,
  });

  if (!clientId) {
    return (
      <DetailPageLayout
        title={language === 'es' ? 'Posición Competitiva' : 'Competitive Position'}
        backLink="/understand"
        backLabel={language === 'es' ? 'Volver a Entender' : 'Back to Understand'}
      >
        <div className="card p-8 text-center">
          <p className="text-text-secondary">{language === 'es' ? 'Seleccione un cliente' : 'Select a client'}</p>
        </div>
      </DetailPageLayout>
    );
  }

  return (
    <DetailPageLayout
      title={language === 'es' ? 'Posición Competitiva' : 'Competitive Position'}
      subtitle={language === 'es' ? 'Análisis SWOT y posicionamiento en el mercado' : 'SWOT analysis and market positioning'}
      backLink="/understand"
      backLabel={language === 'es' ? 'Volver a Entender' : 'Back to Understand'}
    >
      {isLoading && <LoadingContent message="Loading position data..." />}
      {error && <ErrorMessage error={error} />}
      {position && (
        <div className="space-y-6">
          {/* Position Summary */}
          <div className="card p-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-surface-700/50 rounded-lg p-4 text-center">
                <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">
                  {language === 'es' ? 'Posición' : 'Market Rank'}
                </div>
                <div className="text-4xl font-bold text-primary-400">#{position.rank}</div>
              </div>
              <div className="bg-surface-700/50 rounded-lg p-4 text-center">
                <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">
                  {language === 'es' ? 'Participación' : 'Total Share'}
                </div>
                <div className="text-4xl font-bold text-white">
                  {((position.total_share || 0) * 100).toFixed(1)}%
                </div>
              </div>
            </div>
          </div>

          {/* SWOT Analysis */}
          <div className="grid grid-cols-2 gap-6">
            <div className="card p-6 border-l-4 border-success-500">
              <h3 className="text-lg font-semibold text-white mb-4">
                {language === 'es' ? 'Fortalezas' : 'Strengths'}
              </h3>
              <ul className="space-y-2">
                {position.strengths?.map((s: string, idx: number) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-text-secondary">
                    <span className="text-success-400">+</span>
                    {s}
                  </li>
                ))}
              </ul>
            </div>
            <div className="card p-6 border-l-4 border-danger-500">
              <h3 className="text-lg font-semibold text-white mb-4">
                {language === 'es' ? 'Debilidades' : 'Weaknesses'}
              </h3>
              <ul className="space-y-2">
                {position.weaknesses?.map((w: string, idx: number) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-text-secondary">
                    <span className="text-danger-400">-</span>
                    {w}
                  </li>
                ))}
              </ul>
            </div>
            <div className="card p-6 border-l-4 border-primary-500">
              <h3 className="text-lg font-semibold text-white mb-4">
                {language === 'es' ? 'Oportunidades' : 'Opportunities'}
              </h3>
              <ul className="space-y-2">
                {position.opportunities?.map((o: string, idx: number) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-text-secondary">
                    <span className="text-primary-400">*</span>
                    {o}
                  </li>
                ))}
              </ul>
            </div>
            <div className="card p-6 border-l-4 border-warning-500">
              <h3 className="text-lg font-semibold text-white mb-4">
                {language === 'es' ? 'Amenazas' : 'Threats'}
              </h3>
              <ul className="space-y-2">
                {position.threats?.map((t: string, idx: number) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-text-secondary">
                    <span className="text-warning-400">!</span>
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </DetailPageLayout>
  );
}

// Portfolio Health Detail
export function PortfolioHealthDetail() {
  const { language } = useLanguage();
  const { selectedClient } = useClient();
  const clientId = selectedClient?.client_id;

  const { data: health, isLoading, error } = useQuery({
    queryKey: ['client', clientId, 'portfolio', 'health'],
    queryFn: () => apiClient.portfolio.getHealth(clientId!),
    enabled: !!clientId,
  });

  if (!clientId) {
    return (
      <DetailPageLayout
        title={language === 'es' ? 'Salud del Portafolio' : 'Portfolio Health'}
        backLink="/understand"
        backLabel={language === 'es' ? 'Volver a Entender' : 'Back to Understand'}
      >
        <div className="card p-8 text-center">
          <p className="text-text-secondary">{language === 'es' ? 'Seleccione un cliente' : 'Select a client'}</p>
        </div>
      </DetailPageLayout>
    );
  }

  return (
    <DetailPageLayout
      title={language === 'es' ? 'Salud del Portafolio' : 'Portfolio Health'}
      subtitle={language === 'es' ? 'Métricas de salud y recomendaciones del portafolio' : 'Portfolio health metrics and recommendations'}
      backLink="/understand"
      backLabel={language === 'es' ? 'Volver a Entender' : 'Back to Understand'}
    >
      {isLoading && <LoadingContent message="Loading portfolio health..." />}
      {error && <ErrorMessage error={error} />}
      {health && (
        <div className="space-y-6">
          {/* Health Score */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-white mb-4">
              {language === 'es' ? 'Puntuación de Salud' : 'Health Score'}
            </h2>
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-surface-700/50 rounded-lg p-4 text-center">
                <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">
                  {language === 'es' ? 'Puntuación' : 'Score'}
                </div>
                <div className="text-3xl font-bold text-primary-400">{health.health_score}</div>
              </div>
              <div className="bg-surface-700/50 rounded-lg p-4 text-center">
                <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">HHI</div>
                <div className="text-3xl font-bold text-white">{(health.hhi * 100).toFixed(0)}</div>
              </div>
              <div className="bg-surface-700/50 rounded-lg p-4 text-center">
                <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">
                  {language === 'es' ? 'SKUs' : 'SKU Count'}
                </div>
                <div className="text-3xl font-bold text-white">{health.sku_count || health.total_skus || 0}</div>
              </div>
              <div className="bg-surface-700/50 rounded-lg p-4 text-center">
                <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">
                  {language === 'es' ? 'En Riesgo' : 'At Risk'}
                </div>
                <div className="text-3xl font-bold text-danger-400">{health.at_risk_count}</div>
              </div>
            </div>
          </div>

          {/* Product List */}
          {(health as any).products && (health as any).products.length > 0 && (
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-white mb-4">
                {language === 'es' ? 'Productos por Salud' : 'Products by Health'}
              </h2>
              <div className="space-y-3">
                {(health as any).products.map((product: any, idx: number) => (
                  <div key={idx} className="flex items-center gap-4 p-3 bg-surface-700/50 rounded-lg">
                    <span className="text-sm text-white flex-1">{product.name || product.gtin}</span>
                    <div className="flex items-center gap-4">
                      <div>
                        <span className="text-xs text-text-tertiary">{language === 'es' ? 'Salud' : 'Health'}</span>
                        <span className={`ml-2 text-sm font-medium ${
                          product.health_score >= 70 ? 'text-success-400' :
                          product.health_score >= 40 ? 'text-warning-400' : 'text-danger-400'
                        }`}>{product.health_score}</span>
                      </div>
                      <div>
                        <span className="text-xs text-text-tertiary">{language === 'es' ? 'Part.' : 'Share'}</span>
                        <span className="ml-2 text-sm text-primary-400">{((product.volume_share || 0) * 100).toFixed(1)}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {(health as any).recommendations && (health as any).recommendations.length > 0 && (
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-white mb-4">
                {language === 'es' ? 'Recomendaciones' : 'Recommendations'}
              </h2>
              <div className="space-y-3">
                {(health as any).recommendations.map((rec: any, idx: number) => (
                  <div key={idx} className="p-3 bg-surface-700/50 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-white">{rec.name || rec.gtin}</span>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        rec.action === 'keep' ? 'bg-success-500/20 text-success-400' :
                        rec.action === 'monitor' ? 'bg-warning-500/20 text-warning-400' :
                        'bg-danger-500/20 text-danger-400'
                      }`}>{rec.action}</span>
                    </div>
                    <p className="text-sm text-text-secondary">{rec.reason}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </DetailPageLayout>
  );
}

// ============ IMPLICATIONS DETAIL PAGES ============

// Performance Diagnosis Detail
export function PerformanceDiagnosisDetail() {
  const { language } = useLanguage();
  const { selectedClient } = useClient();
  const clientId = selectedClient?.client_id;

  const { data, isLoading, error } = useQuery({
    queryKey: ['client', clientId, 'portfolio', 'due-to'],
    queryFn: () => apiClient.portfolio.getDueTo(clientId!),
    enabled: !!clientId,
  });

  if (!clientId) {
    return (
      <DetailPageLayout
        title={language === 'es' ? 'Diagnóstico de Desempeño' : 'Performance Diagnosis'}
        backLink="/implications"
        backLabel={language === 'es' ? 'Volver a Implicaciones' : 'Back to Implications'}
      >
        <div className="card p-8 text-center">
          <p className="text-text-secondary">{language === 'es' ? 'Seleccione un cliente' : 'Select a client'}</p>
        </div>
      </DetailPageLayout>
    );
  }

  return (
    <DetailPageLayout
      title={language === 'es' ? 'Diagnóstico de Desempeño' : 'Performance Diagnosis'}
      subtitle={language === 'es' ? 'Análisis detallado de factores de volumen' : 'Detailed volume factor analysis'}
      backLink="/implications"
      backLabel={language === 'es' ? 'Volver a Implicaciones' : 'Back to Implications'}
    >
      {isLoading && <LoadingContent message="Analyzing performance..." />}
      {error && <ErrorMessage error={error} />}
      {data && (
        <div className="space-y-6">
          {/* Summary */}
          <div className="card p-6">
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-surface-700/50 rounded-lg p-4 text-center">
                <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">
                  {language === 'es' ? 'Volumen Base' : 'Baseline Volume'}
                </div>
                <div className="text-2xl font-bold text-white">{formatNumber(data.baseline_volume)}</div>
              </div>
              <div className="bg-surface-700/50 rounded-lg p-4 text-center">
                <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">
                  {language === 'es' ? 'Volumen Actual' : 'Actual Volume'}
                </div>
                <div className="text-2xl font-bold text-white">{formatNumber(data.actual_volume)}</div>
              </div>
              <div className="bg-surface-700/50 rounded-lg p-4 text-center">
                <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">
                  {language === 'es' ? 'Cambio' : 'Change'}
                </div>
                <div className={`text-2xl font-bold ${data.volume_change_pct >= 0 ? 'text-success-400' : 'text-danger-400'}`}>
                  {data.volume_change_pct >= 0 ? '+' : ''}{data.volume_change_pct.toFixed(1)}%
                </div>
              </div>
            </div>
          </div>

          {/* Factor Details */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-white mb-4">
              {language === 'es' ? 'Detalle de Factores' : 'Factor Details'}
            </h2>
            <div className="space-y-4">
              {Object.entries(data.drivers).map(([key, value]: [string, any]) => (
                <div key={key} className="p-4 bg-surface-700/50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-medium capitalize">{key.replace('_', ' ')}</span>
                    <span className={`text-lg font-bold ${(value.contribution_pct || 0) >= 0 ? 'text-success-400' : 'text-danger-400'}`}>
                      {(value.contribution_pct || 0) >= 0 ? '+' : ''}{(value.contribution_pct || 0).toFixed(1)}%
                    </span>
                  </div>
                  <div className="h-3 bg-surface-600 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${(value.contribution_pct || 0) >= 0 ? 'bg-success-500' : 'bg-danger-500'}`}
                      style={{ width: `${Math.min(Math.abs(value.contribution_pct || 0) * 5, 100)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </DetailPageLayout>
  );
}

// Opportunity Analysis Detail
export function OpportunityAnalysisDetail() {
  const { language } = useLanguage();
  const { selectedClient } = useClient();
  const clientId = selectedClient?.client_id;

  const { data, isLoading, error } = useQuery({
    queryKey: ['client', clientId, 'opportunities', language],
    queryFn: () => apiClient.opportunities.get(clientId!, language),
    enabled: !!clientId,
  });

  if (!clientId) {
    return (
      <DetailPageLayout
        title={language === 'es' ? 'Análisis de Oportunidades' : 'Opportunity Analysis'}
        backLink="/implications"
        backLabel={language === 'es' ? 'Volver a Implicaciones' : 'Back to Implications'}
      >
        <div className="card p-8 text-center">
          <p className="text-text-secondary">{language === 'es' ? 'Seleccione un cliente' : 'Select a client'}</p>
        </div>
      </DetailPageLayout>
    );
  }

  const impactColors = {
    critical: 'border-l-danger-500',
    high: 'border-l-warning-500',
    medium: 'border-l-primary-500',
    low: 'border-l-gray-500',
  };

  return (
    <DetailPageLayout
      title={language === 'es' ? 'Análisis de Oportunidades' : 'Opportunity Analysis'}
      subtitle={language === 'es' ? 'Todas las oportunidades identificadas' : 'All identified opportunities'}
      backLink="/implications"
      backLabel={language === 'es' ? 'Volver a Implicaciones' : 'Back to Implications'}
    >
      {isLoading && <LoadingContent message="Loading opportunities..." />}
      {error && <ErrorMessage error={error} />}
      {data && (
        <div className="space-y-6">
          {/* Summary */}
          <div className="card p-6">
            <div className="grid grid-cols-5 gap-4">
              <div className="bg-surface-700/50 rounded-lg p-4 text-center">
                <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">Total</div>
                <div className="text-2xl font-bold text-white">{data.opportunitiesFound}</div>
              </div>
              <div className="bg-surface-700/50 rounded-lg p-4 text-center">
                <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">Critical</div>
                <div className="text-2xl font-bold text-danger-400">{data.byImpact?.critical || 0}</div>
              </div>
              <div className="bg-surface-700/50 rounded-lg p-4 text-center">
                <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">High</div>
                <div className="text-2xl font-bold text-warning-400">{data.byImpact?.high || 0}</div>
              </div>
              <div className="bg-surface-700/50 rounded-lg p-4 text-center">
                <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">Medium</div>
                <div className="text-2xl font-bold text-primary-400">{data.byImpact?.medium || 0}</div>
              </div>
              <div className="bg-surface-700/50 rounded-lg p-4 text-center">
                <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">Low</div>
                <div className="text-2xl font-bold text-text-secondary">{data.byImpact?.low || 0}</div>
              </div>
            </div>
          </div>

          {/* All Opportunities */}
          <div className="space-y-4">
            {data.opportunities?.map((opp: any) => (
              <div key={opp.id} className={`card p-6 border-l-4 ${impactColors[opp.impact as keyof typeof impactColors]}`}>
                <div className="flex items-start gap-4">
                  <span className="text-3xl">{opp.icon}</span>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-white">{opp.title}</h3>
                      <span className={`text-xs px-2 py-1 rounded ${
                        opp.impact === 'critical' ? 'bg-danger-500/20 text-danger-400' :
                        opp.impact === 'high' ? 'bg-warning-500/20 text-warning-400' :
                        opp.impact === 'medium' ? 'bg-primary-500/20 text-primary-400' :
                        'bg-gray-500/20 text-text-secondary'
                      }`}>{opp.impact}</span>
                      <span className="text-xs text-text-tertiary">Score: {opp.severityScore}</span>
                    </div>
                    <p className="text-text-secondary mb-3">{opp.description}</p>
                    {opp.action && (
                      <p className="text-sm text-primary-400">
                        <strong>{language === 'es' ? 'Acción:' : 'Action:'}</strong> {opp.action}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </DetailPageLayout>
  );
}

// Sensitivity Analysis Detail
export function SensitivityAnalysisDetail() {
  const { language } = useLanguage();
  const { selectedClient } = useClient();
  const clientId = selectedClient?.client_id;

  const { data: marketData, isLoading } = useQuery({
    queryKey: ['client', clientId, 'market', 'overview'],
    queryFn: () => apiClient.market.getOverview(clientId!),
    enabled: !!clientId,
  });

  if (!clientId) {
    return (
      <DetailPageLayout
        title={language === 'es' ? 'Análisis de Sensibilidad' : 'Sensitivity Analysis'}
        backLink="/implications"
        backLabel={language === 'es' ? 'Volver a Implicaciones' : 'Back to Implications'}
      >
        <div className="card p-8 text-center">
          <p className="text-text-secondary">{language === 'es' ? 'Seleccione un cliente' : 'Select a client'}</p>
        </div>
      </DetailPageLayout>
    );
  }

  const valueGrowth = (marketData as any)?.overview?.value_growth || marketData?.market?.growth_rate || 0;
  const volumeGrowth = (marketData as any)?.overview?.volume_growth || 0;

  return (
    <DetailPageLayout
      title={language === 'es' ? 'Análisis de Sensibilidad' : 'Sensitivity Analysis'}
      subtitle={language === 'es' ? 'Cómo responde el sistema a cambios' : 'How the system responds to changes'}
      backLink="/implications"
      backLabel={language === 'es' ? 'Volver a Implicaciones' : 'Back to Implications'}
    >
      {isLoading && <LoadingContent message="Loading sensitivity data..." />}
      {marketData && (
        <div className="space-y-6">
          {/* Market Trends */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-white mb-4">
              {language === 'es' ? 'Tendencias del Mercado' : 'Market Trends'}
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-surface-700/50 rounded-lg p-4">
                <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">
                  {language === 'es' ? 'Crecimiento en Valor' : 'Value Growth'}
                </div>
                <div className={`text-2xl font-bold ${valueGrowth >= 0 ? 'text-success-400' : 'text-danger-400'}`}>
                  {(valueGrowth * 100).toFixed(1)}%
                </div>
              </div>
              <div className="bg-surface-700/50 rounded-lg p-4">
                <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">
                  {language === 'es' ? 'Crecimiento en Volumen' : 'Volume Growth'}
                </div>
                <div className={`text-2xl font-bold ${volumeGrowth >= 0 ? 'text-success-400' : 'text-danger-400'}`}>
                  {(volumeGrowth * 100).toFixed(1)}%
                </div>
              </div>
            </div>
          </div>

          {/* Competitor Analysis */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-white mb-4">
              {language === 'es' ? 'Movimientos de Competidores' : 'Competitor Movements'}
            </h2>
            <div className="space-y-3">
              {marketData.top_brands?.slice(0, 8).map((brand: any, idx: number) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-surface-700/50 rounded-lg">
                  <span className="text-white">{brand.name}</span>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-text-secondary">{((brand.share || 0) * 100).toFixed(1)}%</span>
                    {brand.growth !== undefined && (
                      <span className={`text-sm ${(brand.growth || 0) >= 0 ? 'text-success-400' : 'text-danger-400'}`}>
                        {(brand.growth || 0) >= 0 ? '+' : ''}{((brand.growth || 0) * 100).toFixed(1)}%
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sensitivity Indicators */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-white mb-4">
              {language === 'es' ? 'Indicadores de Sensibilidad' : 'Sensitivity Indicators'}
            </h2>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-surface-700/50 rounded-lg p-4 text-center">
                <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">
                  {language === 'es' ? 'Elasticidad Precio' : 'Price Elasticity'}
                </div>
                <div className="text-2xl font-bold text-primary-400">-1.8</div>
                <p className="text-xs text-text-tertiary mt-1">{language === 'es' ? 'Elástico' : 'Elastic'}</p>
              </div>
              <div className="bg-surface-700/50 rounded-lg p-4 text-center">
                <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">
                  {language === 'es' ? 'Sens. Ingresos' : 'Income Sensitivity'}
                </div>
                <div className="text-2xl font-bold text-warning-400">0.6</div>
                <p className="text-xs text-text-tertiary mt-1">{language === 'es' ? 'Moderada' : 'Moderate'}</p>
              </div>
              <div className="bg-surface-700/50 rounded-lg p-4 text-center">
                <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">
                  {language === 'es' ? 'Riesgo Recesión' : 'Recession Risk'}
                </div>
                <div className="text-2xl font-bold text-success-400">
                  {language === 'es' ? 'Bajo' : 'Low'}
                </div>
                <p className="text-xs text-text-tertiary mt-1">{language === 'es' ? 'Resiliente' : 'Resilient'}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </DetailPageLayout>
  );
}

// Helper functions
function formatNumber(num: number): string {
  if (num >= 1000000000) return `${(num / 1000000000).toFixed(1)}B`;
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toFixed(0);
}

function formatCurrency(value: number, currency: string): string {
  const formatted = formatNumber(value);
  const symbols: Record<string, string> = { USD: '$', BRL: 'R$', MXN: '$', COP: '$', EUR: '\u20AC' };
  return `${symbols[currency] || currency}${formatted}`;
}
